import { useEffect, useRef, useState, type FormEvent } from "react";
import { Inbox } from "lucide-react";

import type { TrackedLine, TrackResponse } from "@sirdab/returns-shared";
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  EmptyState,
  Input,
} from "@sirdab/ui";

import { useApi, type ApiFailure } from "../api";
import { ErrorState, Field, Money, Timeline } from "../components";
import { formatCount } from "../format/money";
import { useMessages } from "../i18n";
import { buildPath, matchRoute } from "../routes";
import {
  readRememberedReference,
  referenceFromSearch,
  trackingLink,
  writeRememberedReference,
} from "../session";
import { CancelPanel } from "./CancelReturn";
import {
  canCancelLine,
  lineStage,
  lineTone,
  rejectionPanelVisible,
} from "./lineState";
import { OfflineNote, useFailureText } from "./shared";

/** Tone is carried by weight and colour only — no icon, no emoji, no celebration. */
const STATE_TEXT_CLASS = {
  default: "text-caption-quiet text-muted-foreground",
  pending: "text-caption text-foreground",
  closed: "text-caption-quiet text-subtle",
  danger: "text-caption text-destructive",
} as const;

/**
 * Step 8 of docs/08 — tracking. Its own route, because it must be reachable directly and
 * not only after submitting. Same credentials as lookup, and the same refusal to
 * distinguish a wrong reference from a wrong phone.
 *
 * Three things this screen must never do:
 *   - show a bare status. A `rejected` line renders `rejectionReason` IN FULL, in the
 *     server's own bilingual words (CLAUDE.md rule 5) — and ONLY a `rejected` line does.
 *     `cancelled` is the shopper's own withdrawal: it never enters the rejection callout
 *     and never carries blame-assigning copy. `booking_failed` is in flight, not a failure;
 *   - show a grade before the line was inspected — the API withholds it, and we show
 *     whatever it sends in plain language rather than the enum;
 *   - reconcile `qtyExpected` against `qtyReceived`. The gap is the data; both are shown.
 *
 * `received` is given weight in the timeline: it is the moment a returns tool without a
 * 3PL behind it cannot show anyone.
 */

/**
 * What the review step hands over on a successful submit (SAI-1324): enough to open the
 * return without asking for anything again, and the fee so it can be stated one last time.
 * It travels on the history entry, never in browser storage.
 */
export interface TrackHandoff {
  reference: string;
  phone: string;
  feeApplied: number;
}

/** The history state is untrusted: anything not shaped exactly like a handoff is ignored. */
export function readHandoff(state: unknown): TrackHandoff | null {
  if (typeof state !== "object" || state === null) return null;
  const candidate = state as Record<string, unknown>;
  if (
    typeof candidate.reference !== "string" ||
    candidate.reference.trim() === "" ||
    typeof candidate.phone !== "string" ||
    candidate.phone.trim() === "" ||
    typeof candidate.feeApplied !== "number" ||
    !Number.isFinite(candidate.feeApplied)
  ) {
    return null;
  }
  return {
    reference: candidate.reference,
    phone: candidate.phone,
    feeApplied: candidate.feeApplied,
  };
}

/**
 * The one-time confirmation, shown at the top of tracking right after a submit. No refund
 * timeline and no promise that money moves (ADR-003); the fee is stated for the last time.
 */
function SubmittedBanner({
  feeApplied,
}: {
  feeApplied: number;
}): React.JSX.Element {
  const { t } = useMessages();
  return (
    <Alert variant="success" data-submitted-banner>
      <AlertTitle>{t("confirmation.heading")}</AlertTitle>
      <AlertDescription>
        <p>{t("confirmation.what_next")}</p>
        {feeApplied > 0 ? (
          <p className="flex items-baseline justify-between gap-3">
            <span>{t("fee.total_label")}</span>
            <Money halalas={feeApplied} className="font-semibold" />
          </p>
        ) : null}
      </AlertDescription>
    </Alert>
  );
}

function LineCard({ line }: { line: TrackedLine }): React.JSX.Element {
  const { t, text, locale } = useMessages();
  const tone = lineTone(line.state);
  const stage = lineStage(line.state);

  return (
    <li
      data-line-state={line.state}
      data-line-tone={tone}
      data-line-stage={stage ?? undefined}
      data-line-cancellable={canCancelLine(line.state)}
      className="border-border flex flex-col gap-1 border-b py-3 last:border-b-0"
    >
      <p className="text-body text-foreground">{text(line.productName)}</p>
      <p className={STATE_TEXT_CLASS[tone]}>{t(`state.${line.state}`)}</p>
      {stage === "done" ? (
        // The merchant's close-out is neutral: approved, nothing more. No refund or
        // goods outcome is promised, and the line is not framed as a rejection.
        <p className="text-body text-foreground">
          {t("track.approved_headline")}
        </p>
      ) : null}

      <p className="text-caption-quiet text-subtle flex flex-wrap gap-x-3">
        <span>
          {t("track.qty_expected")}{" "}
          <span dir="ltr" className="tabular-nums">
            {formatCount(line.qtyExpected, locale)}
          </span>
        </span>
        {line.qtyReceived !== null ? (
          <span>
            {t("track.qty_received")}{" "}
            <span dir="ltr" className="tabular-nums">
              {formatCount(line.qtyReceived, locale)}
            </span>
          </span>
        ) : null}
      </p>

      {line.grade !== undefined ? (
        <p className="text-caption-quiet text-muted-foreground">
          {t("track.grade_label")}: {t(`grade.${line.grade}`)}
        </p>
      ) : null}

      {rejectionPanelVisible(line.state) &&
      line.rejectionReason !== undefined ? (
        // In full, never truncated, never replaced by the status word alone — and gated on
        // the state, not merely on the field being present, so a cancelled line can never
        // be shown text that reads as a refusal.
        <Alert variant="destructive" className="mt-2">
          <AlertTitle>{t("track.rejection_heading")}</AlertTitle>
          <AlertDescription>{text(line.rejectionReason)}</AlertDescription>
        </Alert>
      ) : null}
    </li>
  );
}

/**
 * How a shopper keeps their reference without us being able to send them anything
 * (SAI-1325). Two offers, both of which work on a phone with no account and no inbox:
 * copy the reference, or copy a link that comes back to this screen with the reference
 * already filled. The link carries the reference and never the phone, so it opens nothing
 * on its own and is safe to keep anywhere.
 *
 * Copying can fail — an insecure origin, a browser that refuses without a gesture it
 * recognises — so the reference stays selectable on screen and a failure says nothing at
 * all rather than claiming a success that did not happen.
 */
function KeepReference({
  merchantSlug,
  reference,
}: {
  merchantSlug: string;
  reference: string;
}): React.JSX.Element {
  const { t } = useMessages();
  const [copied, setCopied] = useState<"reference" | "link" | null>(null);

  const copy = async (what: "reference" | "link"): Promise<void> => {
    const trackPath = buildPath("track", merchantSlug);
    const link =
      typeof window === "undefined"
        ? trackingLink(trackPath, reference)
        : new URL(
            trackingLink(trackPath, reference),
            window.location.origin,
          ).toString();
    try {
      await navigator.clipboard.writeText(
        what === "reference" ? reference : link,
      );
    } catch {
      return;
    }
    setCopied(what);
  };

  return (
    <div className="flex shrink-0 items-center gap-1.5">
      <Button
        type="button"
        variant="secondary"
        size="sm"
        onClick={() => void copy("reference")}
      >
        {copied === "reference" ? t("track.copied") : t("track.copy_reference")}
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => void copy("link")}
      >
        {copied === "link" ? t("track.copied") : t("track.copy_link")}
      </Button>
    </div>
  );
}

function Result({
  data,
  merchantSlug,
  phone,
  justSubmitted,
  onReset,
  onCancelled,
  onRefresh,
}: {
  data: TrackResponse;
  merchantSlug: string;
  /** Null when a track link opened the return and no phone has been typed. */
  phone: string | null;
  /** Set only when arriving straight from a submit; the banner is that moment's confirmation. */
  justSubmitted: TrackHandoff | null;
  onReset: () => void;
  onCancelled: (next: TrackResponse) => void;
  onRefresh: () => void;
}): React.JSX.Element {
  const { t } = useMessages();
  return (
    <div className="flex flex-col gap-6">
      {justSubmitted !== null ? (
        <SubmittedBanner feeApplied={justSubmitted.feeApplied} />
      ) : null}

      <div className="bg-surface-whisper flex flex-col gap-2 rounded-lg p-3">
        <div className="flex flex-wrap items-end justify-between gap-2">
          <div className="min-w-0">
            <p className="text-caption text-subtle uppercase">
              {t("track.reference_label")}
            </p>
            <p dir="ltr" className="text-heading-xs tabular text-foreground">
              {data.reference}
            </p>
          </div>
          <KeepReference
            merchantSlug={merchantSlug}
            reference={data.reference}
          />
        </div>
        <p className="text-caption-quiet text-subtle">{t("track.keep_hint")}</p>
      </div>

      <section className="flex flex-col gap-2">
        <h2 className="text-caption text-subtle uppercase">
          {t("track.lines_heading")}
        </h2>
        {data.lines.length === 0 ? (
          <EmptyState
            icon={Inbox}
            title={t("ui.empty_generic")}
            className="py-6"
          />
        ) : (
          <ul className="flex flex-col">
            {data.lines.map((line) => (
              <LineCard key={line.lineRef} line={line} />
            ))}
          </ul>
        )}
      </section>

      <section className="flex flex-col gap-2">
        <h2 className="text-caption text-subtle uppercase">
          {t("track.timeline_heading")}
        </h2>
        <Timeline events={data.events} />
      </section>

      {/* Withdrawing the return. Offered only while `@sirdab/returns-core` still considers every
          line cancellable — the same condition the server enforces. */}
      <CancelPanel
        merchantSlug={merchantSlug}
        phone={phone}
        data={data}
        onCancelled={onCancelled}
        onRefresh={onRefresh}
      />

      <Button variant="ghost" size="touch" className="w-full" onClick={onReset}>
        {t("action.start_over")}
      </Button>
    </div>
  );
}

/** A return already opened by its track link (`/:merchantSlug/returns/r/:token`). */
export interface TrackLink {
  token: string;
  data: TrackResponse;
}

export function TrackScreen({
  merchantSlug,
  handoff,
  linked,
}: {
  merchantSlug?: string;
  /**
   * A just-submitted return to open straight away. Defaults to whatever the review step
   * left on the current history entry; tests pass it explicitly.
   */
  handoff?: TrackHandoff | null;
  /**
   * Opened by a track link. The token stands in for reference plus phone, so the view
   * opens at once and a refresh re-reads it through the same link.
   */
  linked?: TrackLink | undefined;
}): React.JSX.Element {
  const api = useApi();
  const { t } = useMessages();
  const failureText = useFailureText();

  const slug =
    merchantSlug ??
    (typeof window === "undefined"
      ? ""
      : (matchRoute(window.location.pathname)?.merchantSlug ?? ""));

  const [submitted] = useState<TrackHandoff | null>(() =>
    linked !== undefined
      ? null
      : handoff !== undefined
        ? handoff
        : typeof window === "undefined"
          ? null
          : readHandoff(window.history.state),
  );
  // Three ways a shopper arrives with their reference already in hand, in the order they
  // are trustworthy: straight from a submit, from a link they bookmarked, and from this
  // tab's own memory of the last one (SAI-1325). Only the phone is ever asked for again.
  const [reference, setReference] = useState(
    () =>
      linked?.data.reference ??
      submitted?.reference ??
      (typeof window === "undefined"
        ? ""
        : (referenceFromSearch(window.location.search) ??
          readRememberedReference(slug) ??
          "")),
  );
  const [phone, setPhone] = useState(submitted?.phone ?? "");
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<ApiFailure | null>(null);
  const [data, setData] = useState<TrackResponse | null>(linked?.data ?? null);
  // Cleared by "start over": from then on the form's reference and phone are the key.
  const [linkToken, setLinkToken] = useState<string | null>(
    linked?.token ?? null,
  );
  // The banner belongs to the arrival, not to the reference: "start over" retires it.
  const [showSubmitted, setShowSubmitted] = useState(submitted !== null);

  // Arriving from a submit opens the return at once; the form is only the fallback.
  const opened = useRef(false);
  useEffect(() => {
    if (opened.current || submitted === null) return;
    opened.current = true;
    void refresh();
  }, [submitted]);

  async function submit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setTouched(true);
    if (reference.trim() === "" || phone.trim() === "") return;
    await refresh();
  }

  /**
   * Read the return with the credentials in hand — the form's submit and the way out of a
   * cancel the
   * server refused because the line moved on: the status on screen is stale, so it is
   * replaced rather than argued with. A failed refresh surfaces as the same error state
   * the form uses, never as a blank page.
   */
  async function refresh(): Promise<void> {
    setFailure(null);
    setBusy(true);
    const result =
      linkToken !== null
        ? await api.resolveLink({ merchantSlug: slug, token: linkToken })
        : await api.track({
            merchantSlug: slug,
            reference: reference.trim(),
            phone: phone.trim(),
          });
    setBusy(false);
    if (!result.ok) {
      // Back to the form, which asks for the reference and phone rather than the link.
      setFailure(result);
      setData(null);
      setLinkToken(null);
      return;
    }
    setData(result.data);
    // Kept only once the server has confirmed the pair opens something, so a typo is
    // never what this tab offers back.
    writeRememberedReference({
      merchantSlug: slug,
      reference: result.data.reference,
    });
  }

  return (
    <section className="flex flex-col gap-4">
      <header className="border-border flex flex-col gap-1 border-b pb-4">
        <h1 className="text-heading-sm text-foreground">
          {t("screen.track.title")}
        </h1>
        <p className="text-body text-muted-foreground">
          {t("screen.track.description")}
        </p>
      </header>

      {data !== null ? (
        <Result
          data={data}
          merchantSlug={slug}
          phone={linkToken !== null ? null : phone.trim()}
          justSubmitted={showSubmitted ? submitted : null}
          onReset={() => {
            setShowSubmitted(false);
            setLinkToken(null);
            setData(null);
          }}
          onCancelled={setData}
          onRefresh={() => void refresh()}
        />
      ) : (
        <form
          noValidate
          onSubmit={(event) => void submit(event)}
          className="flex flex-col gap-4"
        >
          <p className="text-body text-muted-foreground">{t("track.intro")}</p>
          <OfflineNote />

          <Field
            label={t("field.reference.label")}
            error={
              touched && reference.trim() === ""
                ? t("validation.reference_required")
                : undefined
            }
            required
          >
            {(props) => (
              <Input
                {...props}
                size="touch"
                dir="ltr"
                type="text"
                autoComplete="off"
                value={reference}
                placeholder={t("field.reference.placeholder")}
                onChange={(event) => setReference(event.target.value)}
              />
            )}
          </Field>

          <Field
            label={t("field.phone.label")}
            hint={t("field.phone.hint")}
            error={
              touched && phone.trim() === ""
                ? t("validation.phone_required")
                : undefined
            }
            required
          >
            {(props) => (
              <Input
                {...props}
                size="touch"
                dir="ltr"
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                value={phone}
                placeholder={t("field.phone.placeholder")}
                onChange={(event) => setPhone(event.target.value)}
              />
            )}
          </Field>

          {failure !== null ? (
            <ErrorState message={failureText(failure)} />
          ) : null}

          <Button type="submit" size="touch" className="w-full" loading={busy}>
            {t("action.track")}
          </Button>
        </form>
      )}
    </section>
  );
}
